import { Flex, Show, theme } from '@everlywell/ui-kit';
import BannerAndCardsV2, {
  parseBannerAndCardsV2Data,
} from 'components/BannerAndCardsV2';
import BannerAndToolCards, {
  parseBannerAndToolCardsData,
} from 'components/BannerAndToolCards';
import CtaBanner from 'components/CtaBanner';
import parseCtaBanner from 'components/CtaBanner/CtaBanner.parser';
import EmailQuiz, { parseQuizData } from 'components/EmailQuiz';
import FaqV2, { parseFaqV2Data } from 'components/FaqV2';
import GridCardList, { parseGridCardListData } from 'components/GridCardList';
import HeroV2 from 'components/HeroV2';
import PressSectionV2 from 'components/Homepage/PressSectionV2';
import parsePressSectionV2Data from 'components/Homepage/PressSectionV2/PressSectionV2.parser';
import { parseHomepageHero } from 'components/HomepageHero';
import HeroCarousel from 'components/HomepageHero';
import JustunoLoader, { parserJustunoLoader } from 'components/JustunoLoader';
import Layout from 'components/Layout';
import MDPModalWrapper from 'components/MDPModalWrapper/MDPModalWrapper';
import { parseDisclaimerWrapper } from 'components/MDPModalWrapper/MDPModalWrapper.parser';
import MedicalConcierge from 'components/MedicalConcierge';
import { parseMedicalConcierge } from 'components/MedicalConcierge/MedicalConcierge.data';
import NavigationAnchor, {
  TOP_SCROLL_OFFSET,
} from 'components/NavigationAnchor';
import {
  getAnchorsSectionsIds,
  parseNavigationAnchor,
} from 'components/NavigationAnchor/NavigationAnchor.parser';
import StickyContextProvider from 'components/NavigationAnchor/Provider/StickyProvider';
import { AsyncPPQModalWrapper } from 'components/PageSections/AsyncProgramPPQ';
import { parseAsyncProgramPPQModalSteps } from 'components/PageSections/AsyncProgramPPQ/AsyncProgramPPQModal.parser';
import { QuizIdsEnum } from 'components/PageSections/AsyncProgramPPQ/constants';
import BiomarkersCardsSection from 'components/PageSections/BiomarkersCardsSection';
import { parseBiomarkersCardsSection } from 'components/PageSections/BiomarkersCardsSection/BiomarkersCardsSection.parser';
import ComparisonTable from 'components/PageSections/ComparisonTable';
import { parseComparisonTable } from 'components/PageSections/ComparisonTable/ComparisonTable.parser';
import DigitalResultsSection from 'components/PageSections/DigitalResultsSection';
import DisclaimerSection from 'components/PageSections/DisclaimerSection';
import { parseDisclaimerSection } from 'components/PageSections/DisclaimerSection/DisclaimerSection.parser';
import FeatureSection from 'components/PageSections/FeatureSection';
import { parseFeatureSection } from 'components/PageSections/FeatureSection/FeatureSection.parser';
import HeroV3 from 'components/PageSections/HeroV3';
import { parseHeroV3 } from 'components/PageSections/HeroV3/HeroV3.parser';
import HeroWithProduct from 'components/PageSections/HeroWithProduct';
import { parseHeroWithProduct } from 'components/PageSections/HeroWithProduct/HeroWithProduct.parser';
import InteractiveBanner from 'components/PageSections/InteractiveBanner';
import { parseInteractiveBanner } from 'components/PageSections/InteractiveBanner/InteractiveBanner.data';
import MultipleCardsCarousel from 'components/PageSections/MultipleCardsCarousel';
import { parseMultipleCardsCarousel } from 'components/PageSections/MultipleCardsCarousel/MultipleCardsCarousel.parser';
import MultipleModals from 'components/PageSections/MultipleModals';
import parseMultipleModals from 'components/PageSections/MultipleModals/MultipleModals.parser';
import { PrePurchaseQuizModalWrapper } from 'components/PageSections/PrePurchaseQuizModal';
import { parsePrePurchaseQuizModalSteps } from 'components/PageSections/PrePurchaseQuizModal/PrePurchaseQuizModal.parser';
import PricingSelectorV2 from 'components/PageSections/PricingSelectorV2';
import { parsePricingSelectorV2 } from 'components/PageSections/PricingSelectorV2/PricingSelectorV2.parser';
import ProgramSection from 'components/PageSections/ProgramSection';
import parseProgramSection from 'components/PageSections/ProgramSection/ProgramSection.parser';
import { PricingCardProps } from 'components/PricingCard';
import PricingSelector, {
  parsePricingSelectorData,
} from 'components/PricingSelector';
import ProductCarousel, {
  parseProductCarouselData,
} from 'components/ProductCarousel';
import ProductTierSection from 'components/ProductTierSection';
import parseProductTierSection from 'components/ProductTierSection/ProductTierSection.parser';
import PromotionBannerV2 from 'components/PromotionBannerV2';
import { parsePromotionBannerV2Data } from 'components/PromotionBannerV2/PromotionBannerV2.parser';
import ReassuranceCarousel from 'components/ReassuranceCarousel';
import { parseReassuranceCarouselData } from 'components/ReassuranceCarousel/ReassuranceCarousel.parser';
import { SEO } from 'components/SEO';
import parseSeoData from 'components/SEO/Seo.parser';
import { parseShapeCards } from 'components/ShapeCards/ShapeCards.parser';
import StepCardList, { parseStepCardListData } from 'components/StepCardList';
import StickyMdpCta, {
  StickyMdpCtaProps,
  parseStickyMdpCta,
} from 'components/StickyMdpCta';
import StoriesCarousel from 'components/StoriesCarousel';
import TabbedBanners from 'components/TabbedBanners';
import { parseTabbedBanners } from 'components/TabbedBanners/TabbedBanners.parser';
import TakeoverBanner from 'components/TakeoverBanner';
import { parseTakeoverBanner } from 'components/TakeoverBanner/TakeoverBanner.parser';
import TrackVisibilityWrapper from 'components/TrackVisibilityWrapper';
import { MDPModalContextProvider } from 'contexts/MDPModal';
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import useAdvertisedPromos from 'utils/hooks/useAdvertisedPromos';
import { ProductDataType } from 'utils/types';
import {
  Hero,
  Metadata,
  Section,
  SeoData,
  Screener,
} from 'utils/types/contentfulTypes';

// TODO: Commented to prevent page scroll to top when changing the selected variant
// import { useQueryParam } from 'use-query-params';
// import { MembershipOptionType } from 'components/PurchaseOptionsDrawerProvider/components/PurchaseOptionsDrawer';

type Props = {
  pageContext: {
    screener?: Screener;
    hero?: Hero;
    metadata?: Metadata;
    sections?: Queries.ContentfulSection001[];
    seo?: SeoData;
    // This needs to be updated to be inside the sections
    allProducts: Record<string, ProductDataType>;
    slug: string;
  };
};

type ComponentParser =
  | ((section: Section, optionalParameter?: any) => any)
  | ((section: Queries.ContentfulSection001) => any);

type ComponentMapping = Record<
  string,
  {
    component: React.FC<any>;
    parser: ComponentParser;
    centerContent?: boolean;
    flexContainer?: boolean;
    width?: string;
  }
>;

/**
 * Mappings for section components
 */
const components: ComponentMapping = {
  BannerAndCardsV2: {
    component: BannerAndCardsV2,
    parser: parseBannerAndCardsV2Data,
    centerContent: true,
  },
  PressSectionV2: {
    component: PressSectionV2,
    parser: parsePressSectionV2Data,
  },
  BannerAndToolCards: {
    component: BannerAndToolCards,
    parser: parseBannerAndToolCardsData,
    centerContent: true,
  },
  StepCardList: {
    component: StepCardList,
    parser: parseStepCardListData,
  },
  ComparisonTable: {
    component: ComparisonTable,
    parser: parseComparisonTable,
  },
  CtaBanner: {
    component: CtaBanner,
    parser: parseCtaBanner,
    centerContent: true,
    flexContainer: true,
  },
  DigitalResultsSection: {
    component: DigitalResultsSection,
    parser: (section: Section) => ({ data: section }),
    centerContent: true,
  },
  FaqV2: { component: FaqV2, parser: parseFaqV2Data, centerContent: true },
  GridCardList: {
    component: GridCardList,
    parser: parseGridCardListData,
    centerContent: true,
  },
  HeroCarousel: {
    component: HeroCarousel,
    parser: parseHomepageHero,
  },
  HeroV3: {
    component: HeroV3,
    parser: parseHeroV3,
  },
  InteractiveBanner: {
    component: InteractiveBanner,
    parser: parseInteractiveBanner,
  },
  MedicalConcierge: {
    component: MedicalConcierge,
    parser: parseMedicalConcierge,
  },
  MultipleCardsCarousel: {
    component: MultipleCardsCarousel,
    parser: parseMultipleCardsCarousel,
  },
  NavigationAnchor: {
    component: NavigationAnchor,
    parser: parseNavigationAnchor,
  },
  PricingSelector: {
    component: PricingSelector,
    parser: parsePricingSelectorData,
    centerContent: false,
  },
  ProductCarousel: {
    component: ProductCarousel,
    parser: parseProductCarouselData,
  },
  ProductTierSection: {
    component: ProductTierSection,
    parser: parseProductTierSection,
    centerContent: true,
  },
  ReassuranceCarousel: {
    component: ReassuranceCarousel,
    parser: parseReassuranceCarouselData,
  },
  StoriesCarousel: {
    component: StoriesCarousel,
    parser: parseShapeCards,
    centerContent: true,
    width: '100%',
  },
  TabbedBanners: {
    component: TabbedBanners,
    parser: parseTabbedBanners,
  },
  TakeoverBanner: {
    component: TakeoverBanner,
    parser: parseTakeoverBanner,
  },
  HeroWithProduct: {
    component: HeroWithProduct,
    parser: parseHeroWithProduct,
  },
  ProgramSection: {
    component: ProgramSection,
    parser: parseProgramSection,
    centerContent: true,
  },
  DisclaimerSection: {
    component: DisclaimerSection,
    parser: parseDisclaimerSection,
    centerContent: true,
  },
  EmailQuiz: {
    component: EmailQuiz,
    parser: parseQuizData,
  },
  JustunoLoader: {
    component: JustunoLoader,
    parser: parserJustunoLoader,
  },
  PricingSelectorV2: {
    component: PricingSelectorV2,
    parser: parsePricingSelectorV2,
  },
  MultipleModals: {
    component: MultipleModals,
    parser: parseMultipleModals,
  },
  PromotionBannerV2: {
    component: PromotionBannerV2,
    parser: parsePromotionBannerV2Data,
  },
  FeatureSection: {
    component: FeatureSection,
    parser: parseFeatureSection,
    centerContent: true,
    width: '100%',
  },
  BiomarkersCardsSection: {
    component: BiomarkersCardsSection,
    parser: parseBiomarkersCardsSection,
  },
};

const MembershipDetailsPage = ({
  pageContext,
}: Props): React.ReactElement<Props> | null => {
  const { allProducts, hero, screener, sections, seo } = pageContext;
  const hideStickyCta = pageContext.metadata?.context?.hideStickyCta;
  // Used to determine if the sticky CTA should be shown.
  // Threshold is 700px, which is about the height of the first section of the pages
  const [ref, isEmptyDivInView, entry] = useInView({
    rootMargin: '700px',
  });

  const [showSticky, setShowSticky] = useState(false);
  const [selectedPricingOption, setSelectedPricingOption] =
    useState<PricingCardProps>();
  const [stickyCtaSectionProps, setStickyCtaSectionProps] =
    useState<StickyMdpCtaProps>();
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isScreenerOpen, setIsScreenerOpen] = useState(false);
  const { anchorsSectionsIds, mainAnchorSectionId } = getAnchorsSectionsIds(
    sections as Queries.ContentfulSection001[],
  );
  const [isLoading, setIsLoading] = useState(true);

  const heroMetadata = hero?.metadata;
  const screenerProps = parseDisclaimerWrapper(screener);
  const hasPrePurchaseQuiz = sections?.some(
    (section) => section.componentName === 'PrePurchaseQuiz',
  );

  const foundAsyncProgramPPQ = sections?.find(
    (section) => section.componentName === 'AsyncProgramPPQ',
  );

  const hasAsyncProgramPPQ = !!foundAsyncProgramPPQ;

  useEffect(() => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.VIEWED_PAGE,
      data: {
        Page: ANALYTICS.LABELS.MDP.EW_PLUS,
      },
    });
  }, []);

  useEffect(() => {
    if (!sections || !Array.isArray(sections)) {
      return;
    }

    if (hideStickyCta) {
      return;
    }

    // Check if there is a sticky CTA section
    const stickyCtaSection = (sections as Queries.ContentfulSection001[])?.find(
      (section) =>
        section?.componentName?.toLowerCase() === 'StickyCTA'.toLowerCase(),
    );

    if (stickyCtaSection) {
      const stickyCtaMetadata = stickyCtaSection?.metadata;
      const stickyMpdCtaProps = parseStickyMdpCta({
        metadata: stickyCtaMetadata as unknown as Queries.ContentfulMetadata001,
        option: selectedPricingOption,
      });

      setStickyCtaSectionProps(stickyMpdCtaProps);
    } else {
      // The is not the preferred way to do this, it relies on having the HeroV2 Section. Not all pages have this section.
      // When there is a Sticky CTA section (previous if block) this will be ignored.
      const stickyMpdCtaProps = parseStickyMdpCta({
        metadata: heroMetadata as unknown as Queries.ContentfulMetadata001,
        option: selectedPricingOption,
      });
      setStickyCtaSectionProps(stickyMpdCtaProps);
    }
  }, [heroMetadata, hideStickyCta, sections, selectedPricingOption]);

  useEffect(() => {
    if (entry?.isIntersecting) {
      setHasScrolled(true);
    }
  }, [entry]);

  useEffect(() => {
    setShowSticky(hasScrolled && !isEmptyDivInView);
  }, [isEmptyDivInView, hasScrolled]);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  const setPricingOption = (option: PricingCardProps) => {
    if (option) {
      setSelectedPricingOption(option);
    }
  };

  const { advertisedPromos } = useAdvertisedPromos();

  return (
    <MDPModalContextProvider
      hashId={screenerProps?.metadata?.metadata_id as string}
    >
      <StickyContextProvider
        anchorsSectionsIds={anchorsSectionsIds}
        mainSectionId={mainAnchorSectionId}
      >
        {seo ? <SEO {...parseSeoData(seo)} /> : <SEO />}
        <Layout isV2Page>
          {/* Empty div used to track scrolling and show sticky cta */}
          <div ref={ref}></div>
          {hero ? (
            <TrackVisibilityWrapper
              label={ANALYTICS.MDP.HERO}
              slug={hero.displayName}
              flexContainer
              centerContent
            >
              <HeroV2
                sectionHeaderIconName={heroMetadata?.context?.icons?.header}
                sectionHeaderText={hero?.displayName}
                headline={heroMetadata?.title}
                bulletPoints={heroMetadata?.description}
                backgroundColor={heroMetadata?.theme?.backgroundColor}
                desktopBackgroundImage={
                  hero?.images?.[0]?.desktopImage?.gatsbyImageData
                }
                mobileBackgroundImage={
                  hero?.images?.[0]?.mobileImage?.gatsbyImageData
                }
                desktopBackgroundImageAltText={
                  hero?.images?.[0]?.desktopImageAltText
                }
                mobileBackgroundImageAltText={
                  hero?.images?.[0]?.mobileImageAltText
                }
                cta={heroMetadata?.ctas?.[0]}
                disclaimer={heroMetadata?.context?.hero_disclaimer}
              ></HeroV2>
            </TrackVisibilityWrapper>
          ) : null}

          <Flex
            // Hide scroll when loading to prevent flickering while css is being applied
            overflowX={isLoading ? 'hidden' : undefined}
            display="flex"
            flexDirection="column"
            gap={[10, null, 20]}
            sx={{
              '& > div > div, & > div > section': {
                scrollMarginTop: [`${TOP_SCROLL_OFFSET}px`, null, '58px'],
              },
              '& > :last-child': {
                // Add margin to the last child to prevent it from sticking to the bottom of the page. Gap doesn't add space to the last child.
                marginBottom: ['10 !important', null, '20 !important'],
              },
            }}
          >
            {/* Add sections here */}
            {sections?.map((section, index) => {
              if (!section.componentName) {
                return null;
              }

              const sectionFLags = section?.metadata?.flags;
              const isSectionHidden = sectionFLags?.some(
                (flag) =>
                  flag?.displayText === 'hideSection' && flag?.value === true,
              );

              if (isSectionHidden) {
                return null;
              }

              // Get the component from the mapping
              const SectionComponent =
                components[section.componentName as keyof typeof components];

              if (!SectionComponent || !SectionComponent.parser || !section) {
                return null;
              }

              let componentProps = SectionComponent.parser(
                // @ts-ignore
                section,
                allProducts,
              );

              if (section.componentName === 'PricingSelector') {
                componentProps = components.PricingSelector.parser(
                  // @ts-ignore
                  section,
                  setPricingOption,
                );
              }

              if (section.componentName === 'ProductCarousel') {
                componentProps = components.ProductCarousel.parser(
                  // @ts-ignore
                  section,
                  advertisedPromos,
                );
              }

              if (section.componentName === 'ProgramSection') {
                componentProps = components.ProgramSection.parser(
                  // @ts-ignore
                  section,
                  advertisedPromos,
                );
              }

              if (SectionComponent) {
                return (
                  <TrackVisibilityWrapper
                    label={
                      section.componentName || 'No component name provided'
                    }
                    slug={section.displayName || 'No display name provided'}
                    centerContent={!!SectionComponent?.centerContent}
                    flexContainer={!!SectionComponent?.flexContainer}
                    width={SectionComponent?.width}
                    key={`${section.componentName}-${index}`}
                    id={section.metadata?.metadata_id || undefined}
                  >
                    {React.createElement(
                      SectionComponent.component,
                      componentProps,
                    )}
                  </TrackVisibilityWrapper>
                );
              }

              return null;
            })}
          </Flex>

          {screenerProps && (
            <MDPModalWrapper
              {...screenerProps}
              onModalStateUpdate={(isOpen) => setIsScreenerOpen(isOpen)}
            />
          )}
          {/*
          The contentful data for this modal should be parsed and and use here

          Parser usage: needs to updated to parse the data
         */}
          {hasPrePurchaseQuiz ? (
            <PrePurchaseQuizModalWrapper
              {...parsePrePurchaseQuizModalSteps()}
            />
          ) : null}

          {hasAsyncProgramPPQ ? (
            <AsyncPPQModalWrapper
              {...parseAsyncProgramPPQModalSteps(
                foundAsyncProgramPPQ?.metadata?.metadata_id as QuizIdsEnum,
              )}
            />
          ) : null}

          <Show breakpoint={`(max-width: ${theme.breakpoints.largeTablet})`}>
            {/* Hide Sticky CTA if the screener modal is open */}
            {showSticky && stickyCtaSectionProps && !isScreenerOpen ? (
              <StickyMdpCta {...stickyCtaSectionProps} />
            ) : null}
          </Show>
        </Layout>
      </StickyContextProvider>
    </MDPModalContextProvider>
  );
};

export default MembershipDetailsPage;

export { Head } from 'utils/helpers/templateHelpers';
